import classNames from "clsx";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { HiCheckCircle, HiMail, HiXCircle } from "react-icons/hi";

import { useUser } from "@shared/auth";
import { Button, Text, Alert } from "@shared/elements";

import { TUpdateNewsletterSubscription } from "__generated__/types";

import { MESSAGE_PREFIX } from "src/constants";
import updateNewsletterSubscription from "src/lib/graphql/update-newsletter-subscription";


enum NEWSLETTER_STATUSES {
  ON,
  OFF,
  SUBSCRIBING,
  SUBSCRIBED,
  FAILED,
}

type NewsletterCardProps = {
  className?: string;
  trackingSourceName?: string;
};

export default function NewsletterCard({
  className,
  trackingSourceName = "call-grid:newsletter-card",
  ...rest
}: NewsletterCardProps) {
  const t = useTranslations(`${MESSAGE_PREFIX}components.NewsletterCard`);
  const user = useUser();
  const [error, setError] = useState(null);

  const [newsletterStatus, setNewsletterStatus] = useState(
    user.data?.newsletter === true
      ? NEWSLETTER_STATUSES.ON
      : NEWSLETTER_STATUSES.OFF,
  );

  const onSubscribe = async () => {
    setNewsletterStatus(NEWSLETTER_STATUSES.SUBSCRIBING);
    const result: TUpdateNewsletterSubscription =
      await updateNewsletterSubscription({
        active: true,
        source: trackingSourceName,
      });

    if (result.user.newsletter) {
      setNewsletterStatus(NEWSLETTER_STATUSES.SUBSCRIBED);
    } else {
      setNewsletterStatus(NEWSLETTER_STATUSES.FAILED);
      setError("Unable to subscribe to newsletter. Please try again.");
    }
  };

  return (
    <div
      className={classNames(
        "h-full flex flex-col justify-between bg-violet-100 p-6 rounded-lg",
        className,
      )}
      {...rest}
    >
      <div>
        <Text
          as="p"
          size="lg"
          weight="medium"
          className="text-violet-800"
          color="custom"
        >
          {t("title")}
        </Text>
        <Text
          as="p"
          size="sm"
          weight="regular"
          className="text-violet-800 mt-2"
          color="custom"
        >
          {t("description")}
        </Text>
        {newsletterStatus === NEWSLETTER_STATUSES.SUBSCRIBED ? (
          <Alert color="green" className="mt-5" Icon={HiCheckCircle}>
            {t("keepPosted")}
          </Alert>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="mr-2 mt-5"
            inProgress={newsletterStatus === NEWSLETTER_STATUSES.SUBSCRIBING}
            onClick={onSubscribe}
            disabled={newsletterStatus === NEWSLETTER_STATUSES.SUBSCRIBING}
          >
            {newsletterStatus !== NEWSLETTER_STATUSES.SUBSCRIBING && (
              <HiMail className="-ml-1 mr-3 inline-block" size="1.25rem" />
            )}
            {t("btnNotifyMe")}
          </Button>
        )}
        {error && (
          <Alert color="red" className="mt-5" Icon={HiXCircle}>
            {error}
          </Alert>
        )}
      </div>
    </div>
  );
}

NewsletterCard.messages = [`${MESSAGE_PREFIX}components.NewsletterCard`];
